/*
 * Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
 * This software is the proprietary information of Irian Software Development SRL.
 * Use is subject to license and non-disclosure terms
 */

import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { RightModel } from '../user/right.model';
import { Subject } from 'rxjs';
import { RightsAndRolesService } from './rights-and-roles.service';
import { RightsAndRolesStateModel } from './models/rights-and-roles-state.model';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { UserModel } from '../user/user.model';
import { RoleWithRightsVo } from '../roles/models/role-with-rights-vo.model';

@Component({
  selector: 'auth-user-inputs-and-roles-view',
  templateUrl: './user-inputs-and-roles-view.component.html',
  styleUrls: ['./user-inputs-and-roles-view.component.scss'],
  providers: [RightsAndRolesService],
})
export class UserInputsAndRolesViewComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @Input() userData: UserModel | undefined = undefined;
  @Input() roleWithRightsVOs: RoleWithRightsVo[] = [];
  @Input() firstSignUp = false;
  @Input() emailValue: string | null = null;
  @Input() showPasswords = false;

  @Output() save: Subject<any> = new Subject();

  @ViewChild('form') userDataForm: NgForm | null = null;

  id: string | undefined = undefined;
  userForm: UntypedFormGroup | undefined = undefined;
  formArray: UntypedFormArray = new UntypedFormArray([]);
  rights: RightModel[] | undefined = undefined;
  state: RightsAndRolesStateModel | undefined = undefined;
  isNewUser = false;
  readOnlyTrueValue = true;
  additionalRightsRowName: string | undefined = undefined;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private rightsAndRolesService: RightsAndRolesService,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService
  ) {
    this.userForm = new UntypedFormGroup({});
  }

  ngOnInit() {
    this.userForm?.addControl('uuid', new UntypedFormControl());
    this.userForm?.addControl('owner', new UntypedFormControl());
    this.userForm?.addControl('name', new UntypedFormControl());
    this.userForm?.addControl('email', new UntypedFormControl());
    this.userForm?.addControl(
      'userPhoneDTO',
      new UntypedFormGroup({ phoneNumber: new UntypedFormControl() })
    );
    this.userForm?.addControl('password', new UntypedFormControl());
    this.userForm?.addControl('passwordConfirmation', new UntypedFormControl());
    this.userForm?.addControl('lang', new UntypedFormControl());
    this.userForm?.addControl(
      'companySuperAdmin',
      new UntypedFormControl(false)
    );
    if (!this.isNewUser) {
      this.userForm?.addControl('oldPassword', new UntypedFormControl());
    }
    this.userForm?.addControl('userRights', this.formArray);
    this.translateService.stream(['auth.role.otherRights']).subscribe((res) => {
      this.additionalRightsRowName = res['auth.role.otherRights'];
    });
  }

  ngOnChanges() {
    if (this.userData && this.roleWithRightsVOs) {
      this.rights = this.userData.userRights;
      this.copyRightsToFormArray(this.rights);
      this.userForm?.patchValue(this.userData);
      this.roleWithRightsVOs.push(this.getAdditionalRightRow());
      this.isNewUser = this.userData.isNewUser;
      this.state = this.rightsAndRolesService.getStateFromInitialRights(
        this.rights
      );
    }
  }

  onSubmit() {
    this.rights = this.rightsAndRolesService.getFinalRightsFromState(
      this.state
    );

    this.copyRightsToFormArray(this.rights);
    this.markAllAsDirty();
    this.userDataForm?.form.markAllAsTouched();
    this.userForm?.updateValueAndValidity({ emitEvent: true });

    if (this.userForm?.valid && this.userDataForm?.valid) {
      this.save.next(this.userForm.getRawValue());
    }
  }

  onRoleValueChanged(event: any, role: RoleWithRightsVo) {
    const checked: boolean = event.checked;

    if (role.uuid && role.uuid.length > 0) {
      if (checked) {
        this.state = this.rightsAndRolesService.addRole(role, this.state);
      } else {
        this.state = this.rightsAndRolesService.removeRole(role, this.state);
      }
    } else {
      //additional rights
      this.state =
        this.rightsAndRolesService.markAllAdditionalRightsWithSelected(
          this.state,
          checked
        );
    }
  }

  ngAfterViewInit(): void {
    this.userDataForm?.valueChanges?.pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.userForm?.patchValue({
          ...res,
          uuid: this.userData?.uuid,
          userPhoneDTO: {
            phoneNumber: res.phoneNumber,
          },
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  private markAllAsDirty() {
    Object.keys(this.userForm?.controls).forEach((key) => {
      this.userForm?.controls[key].markAsDirty();
    });
  }

  private getAdditionalRightRow() {
    const additionalRightRow: RoleWithRightsVo = {
      uuid: '',
      name: this.additionalRightsRowName,
      description: '',
      owner: '',
      platformRole: false,
      selected: false,
      rightDefinitions: [],
    };

    return additionalRightRow;
  }

  private addRightToFormArray(right: RightModel) {
    this.formArray.push(
      this.formBuilder.group({
        rightInstanceUuid: new UntypedFormControl(right.rightInstanceUuid),
        rightName: new UntypedFormControl(right.rightName),
        rightLabel: new UntypedFormControl(right.rightLabel),
        selected: new UntypedFormControl(right.selected),
        description: new UntypedFormControl(right.description),
        roleDefinitionUuid: new UntypedFormControl(right.roleDefinitionUuid),
        roleRightDefinitionUuid: new UntypedFormControl(
          right.roleRightDefinitionUuid
        ),
      })
    );
  }

  private copyRightsToFormArray(rights: RightModel[]) {
    this.formArray.clear();
    rights.forEach((right) => {
      this.addRightToFormArray(right);
    });
  }
}
